import { useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { OverrideReasonSchema } from '@/features/compliance-studio/machinery-directive/components/HighRiskOverrideDialog'

/** Initial Yes/No from automated result and stored override (see product rules). */
export function deriveHighRiskDialogAnswer(
  isHighRisk: boolean | null | undefined,
  storedReason: string | null | undefined
): 'yes' | 'no' {
  const hasOverride = Boolean(storedReason?.trim())
  if (isHighRisk === true) {
    return hasOverride ? 'no' : 'yes'
  }
  if (isHighRisk === false) {
    return hasOverride ? 'yes' : 'no'
  }
  return hasOverride ? 'no' : 'yes'
}

/** User’s choice differs from the LLM classification (either direction). */
export function userDisagreesWithLlm(
  highRiskAnswer: 'yes' | 'no',
  isHighRisk: boolean | null | undefined
): boolean {
  const userSaysHighRisk = highRiskAnswer === 'yes'
  const llmSaysHighRisk = isHighRisk === true
  return userSaysHighRisk !== llmSaysHighRisk
}

type ProductMetadataHighRiskReasonDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  isHighRisk: boolean | null | undefined
  storedReason: string | null | undefined
  isPending: boolean
  onSave: (reason: string | null) => void
}

export function ProductMetadataHighRiskReasonDialog({
  open,
  onOpenChange,
  isHighRisk,
  storedReason,
  isPending,
  onSave,
}: ProductMetadataHighRiskReasonDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const answer = deriveHighRiskDialogAnswer(isHighRisk, storedReason)
  const [highRiskAnswer, setHighRiskAnswer] = useState<'yes' | 'no'>(answer)
  const [draft, setDraft] = useState('')
  const draftBackupRef = useRef('')

  useEffect(() => {
    if (!open) {
      return
    }
    const baseline = storedReason?.trim() ?? ''
    draftBackupRef.current = baseline
    setHighRiskAnswer(answer)
    const disagreesInitially = userDisagreesWithLlm(answer, isHighRisk)
    setDraft(disagreesInitially ? baseline : '')
  }, [open, storedReason, isHighRisk, answer])

  const storedTrimmed = storedReason?.trim() ?? ''
  const trimmedDraft = draft.trim()
  const draftValid = OverrideReasonSchema.safeParse(trimmedDraft).success
  const disagrees = userDisagreesWithLlm(highRiskAnswer, isHighRisk)
  /** User classifies as high-risk while the automated result was not high-risk (incl. unknown). */
  const showMoveToHighRiskWarning = highRiskAnswer === 'yes'

  const canSave =
    !isPending &&
    (disagrees
      ? draftValid && trimmedDraft !== storedTrimmed
      : storedTrimmed !== '')

  const reasonPlaceholderKey =
    isHighRisk === true
      ? 'settings.productMetadata.highRiskAnalysis.dialog.reasonPlaceholderAutomatedHighRisk'
      : 'settings.productMetadata.highRiskAnalysis.dialog.reasonPlaceholderAutomatedNotHighRisk'

  const handleSave = () => {
    if (!disagrees) {
      onSave(null)
      return
    }
    if (!draftValid) {
      return
    }
    onSave(trimmedDraft)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='gap-4 sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='text-base'>
            {t('settings.productMetadata.highRiskAnalysis.dialog.title')}
          </DialogTitle>
        </DialogHeader>
        <fieldset className='space-y-2'>
          <legend className='text-sm font-medium'>
            {t(
              'settings.productMetadata.highRiskAnalysis.dialog.highRiskStateQuestion'
            )}
          </legend>
          <RadioGroup
            value={highRiskAnswer}
            onValueChange={(value) => {
              const next = value as 'yes' | 'no'
              const nextDisagrees = userDisagreesWithLlm(next, isHighRisk)
              const prevDisagrees = userDisagreesWithLlm(
                highRiskAnswer,
                isHighRisk
              )
              if (nextDisagrees && !prevDisagrees) {
                setDraft(draftBackupRef.current)
              } else if (!nextDisagrees && prevDisagrees) {
                draftBackupRef.current = draft
                setDraft('')
              }
              setHighRiskAnswer(next)
            }}
            disabled={isPending}
            className='flex flex-col gap-3 pt-1'
          >
            <div className='flex items-center gap-2'>
              <RadioGroupItem value='yes' id='high-risk-state-yes' />
              <Label
                htmlFor='high-risk-state-yes'
                className='cursor-pointer font-normal'
              >
                {t('common:yes')}
              </Label>
            </div>
            <div className='flex items-center gap-2'>
              <RadioGroupItem value='no' id='high-risk-state-no' />
              <Label
                htmlFor='high-risk-state-no'
                className='cursor-pointer font-normal'
              >
                {t('common:no')}
              </Label>
            </div>
          </RadioGroup>
        </fieldset>
        {showMoveToHighRiskWarning && (
          <Alert variant='warning'>
            <AlertTitle>
              {t(
                'settings.productMetadata.highRiskAnalysis.selfCertWarningTitle'
              )}
            </AlertTitle>
            <AlertDescription className='block'>
              <Trans
                i18nKey='machineryDirectiveCompliance:settings.productMetadata.highRiskAnalysis.selfCertWarningDescription'
                components={{
                  a: (
                    <a
                      className='font-medium text-amber-900 underline underline-offset-4 hover:text-amber-950 dark:text-yellow-100 dark:hover:text-yellow-50'
                      href='mailto:info@artem.earth'
                    />
                  ),
                }}
              />
            </AlertDescription>
          </Alert>
        )}
        {disagrees && (
          <div className='space-y-2'>
            <Label htmlFor='high-risk-reason-text'>
              {t(
                'settings.productMetadata.highRiskAnalysis.dialog.reasonLabel'
              )}
            </Label>
            <Textarea
              id='high-risk-reason-text'
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={t(reasonPlaceholderKey)}
              rows={4}
              disabled={isPending}
              className='resize-none'
            />
          </div>
        )}
        <DialogFooter className='gap-2 sm:justify-end'>
          <Button
            type='button'
            variant='outline'
            disabled={isPending}
            onClick={() => onOpenChange(false)}
          >
            {t('settings.productMetadata.highRiskAnalysis.dialog.cancel')}
          </Button>
          <Button type='button' disabled={!canSave} onClick={handleSave}>
            {isPending
              ? t('settings.productMetadata.highRiskAnalysis.dialog.saving')
              : t('settings.productMetadata.highRiskAnalysis.dialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
